'use client';

import { useEffect, useRef } from 'react';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';

/**
 * ChatWidget Component
 *
 * Injects the MarketBetter chat widget into pages.
 * Uses the same widget injection mechanism as customer websites.
 *
 * The widget script is loaded from: {API_ENDPOINT}/{CHATBOT_TOKEN}.js?referrer=...&pageUrl=...
 *
 * Environment Variables Required:
 * - NEXT_PUBLIC_API_ENDPOINT: API base URL (e.g., https://www.marketbetter.ai)
 * - NEXT_PUBLIC_DEFAULT_CHATBOT_TOKEN: Default chatbot token for public pages
 */
const ChatWidget: React.FC = () => {
  const chatToken = process.env.NEXT_PUBLIC_DEFAULT_CHATBOT_TOKEN;
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const initAttemptedRef = useRef(false);

  useEffect(() => {
    const apiEndpoint = getPublicApiBaseUrl();
    // Don't initialize if required env vars are missing
    if (!apiEndpoint || !chatToken) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[ChatWidget] Missing required environment variables:',
          {
            NEXT_PUBLIC_API_ENDPOINT: apiEndpoint || 'MISSING',
            NEXT_PUBLIC_DEFAULT_CHATBOT_TOKEN: chatToken || 'MISSING',
          }
        );
      }
      return;
    }

    // Prevent duplicate initialization within the same component lifecycle
    if (typeof window === 'undefined' || initAttemptedRef.current) {
      return;
    }

    // Mark initialization as attempted
    initAttemptedRef.current = true;

    // Check if widget script already exists (prevents duplicate injections across component remounts)
    const existingScript = document.querySelector(`script[src*="${chatToken}.js"]`);
    if (existingScript) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ChatWidget] Widget script already loaded, skipping re-injection');
      }
      scriptRef.current = existingScript as HTMLScriptElement;
      return;
    }

    // Load the embed script WITHOUT per-page referrer/pageUrl query params.
    // Cloudflare keys its cache on the full URL incl. query, so appending the page
    // URL created one cache entry per page — defeating purge-on-save and letting a
    // page serve a stale (e.g. pre-suppress) script. The widget script already reads
    // document.referrer / window.location.href at runtime when these are absent
    // (see chatbotJS getScriptParams fallback), so dropping them changes nothing at
    // runtime while collapsing the cache to one entry per token.
    const widgetScriptUrl = `${apiEndpoint}/${chatToken}.js`;

    // Create and inject script tag (matching customer embed code pattern)
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = widgetScriptUrl;
    script.async = true;
    script.setAttribute('data-chatbot-token', chatToken);

    script.onload = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ChatWidget] Widget script loaded:', widgetScriptUrl);
      }
    };

    script.onerror = (e) => {
      console.error('[ChatWidget] Failed to load widget script:', widgetScriptUrl, e);
      initAttemptedRef.current = false; // Allow retry on error
    };

    // Append to head (matching customer embed pattern)
    const head = document.getElementsByTagName('head')[0];
    if (head) {
      head.appendChild(script);
      scriptRef.current = script;
    }

    // Cleanup function
    return () => {
      // Note: In a SPA with client-side routing, we typically want to keep the widget
      // across navigation. However, if this component is truly unmounted (not just re-rendered),
      // we should clean up to prevent memory leaks.

      // Only remove if the component is actually being destroyed (not just re-rendering)
      // The widget script itself handles its own DOM cleanup (iframe, button, tooltip)
      // when the script is removed from the page

      if (process.env.NODE_ENV === 'development') {
        console.log('[ChatWidget] Component unmounting - widget will persist for SPA navigation');
      }

      // Reset attempt flag to allow re-initialization if component remounts
      initAttemptedRef.current = false;
    };
  }, [chatToken]);

  // Return null since we're injecting script directly
  return null;
};

export default ChatWidget;
